import React, { useState } from "react";
import { useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useUIContext } from "../../../context/UIContext";
import SurveySettingTable from "./components/SurveySettingTable";
import MakingSurvey from "./components/MakingSurvey";
import useSurveys from "../../../hooks/useSurveys";
import useSurveyQuestions from "../../../hooks/useSurveyQuestions";

export default function SurveySetting() {
  const { t } = useTranslation();
  const location = useLocation();
  const paths = location.pathname.split("/");
  const schoolId = paths[1];
  const { handleToastCenterTop, setToastConfirmModalState } = useUIContext();

  const [selectedSurvey, setSelectedSurvey] = useState(null);
  const [editingSurveys, setEditingSurveys] = useState({}); // 수정 중인 설문들의 임시 상태
  const [newSurvey, setNewSurvey] = useState({
    survey_type: "bySubject",
    title: "",
    response_target: "student",
    status: "respondable",
    allow_question_add: true,
    is_anonymous: false,
    deadline: 0,
    teacher_editable: true,
    no_response_action: "none",
    include_tag: false,
    theme: "classic",
  });

  // API 호출
  const { surveysQuery, createSurvey, updateSurvey, deleteSurvey } = useSurveys(
    {
      schoolid: schoolId,
    }
  );

  // 질문 개수 확인용 (선택된 설문이 있을 때만 활성화)
  const { surveyQuestionsQuery } = useSurveyQuestions({
    schoolid: schoolId,
    survey_id: selectedSurvey?.id,
  });

  const surveys = surveysQuery.data || [];
  const isLoading = surveysQuery.isLoading;
  const isError = surveysQuery.error;

  // 각 mutation의 로딩 상태
  const isCreating = createSurvey.isLoading;
  const isUpdating = updateSurvey.isLoading;
  const isDeleting = deleteSurvey.isLoading;

  const handleRowClick = (survey) => {
    setSelectedSurvey(survey);
  };

  const handleInputChange = (field, value) => {
    setNewSurvey((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  // 설문 수정 시 임시 상태 업데이트 (자동저장 방지)
  const handleSurveyChange = (id, field, value) => {
    setEditingSurveys((prev) => ({
      ...prev,
      [id]: {
        ...prev[id],
        [field]: value,
      },
    }));
  };

  // 저장 버튼 클릭 시 실제 업데이트
  const handleSurveySave = (id) => {
    const originalSurvey = surveys.find((s) => s.id === id);
    const editedData = editingSurveys[id];

    if (originalSurvey && editedData) {
      updateSurvey.mutate(
        {
          ...originalSurvey,
          ...editedData,
        },
        {
          onSuccess: () => {
            handleToastCenterTop(
              t("common.survey.successUpdate"),
              3000,
              "#28a745"
            );

            // 선택된 설문이 업데이트된 설문과 같다면 selectedSurvey도 업데이트
            if (selectedSurvey?.id === id) {
              setSelectedSurvey((prev) => ({ ...prev, ...editedData }));
            }

            // 임시 상태 초기화
            setEditingSurveys((prev) => {
              const newState = { ...prev };
              delete newState[id];
              return newState;
            });
          },
          onError: (error) => {
            console.error("설문 수정 오류:", error);
            handleToastCenterTop(
              t("common.survey.failUpdate") + ": " + error.message,
              3000,
              "#dc3545"
            );
          },
        }
      );
    }
  };

  // 현재 표시할 설문 데이터 (원본 + 수정중인 데이터)
  const getDisplaySurvey = (survey) => {
    return {
      ...survey,
      ...editingSurveys[survey.id],
    };
  };

  const handleAddSurvey = () => {
    if (!newSurvey.survey_type || !newSurvey.title) {
      handleToastCenterTop(t("common.survey.requiredFields"), 3000, "#dc3545");
      return;
    }

    createSurvey.mutate(
      {
        ...newSurvey,
        schoolid: schoolId,
      },
      {
        onSuccess: () => {
          handleToastCenterTop(
            t("common.survey.successCreate"),
            3000,
            "#28a745"
          );
          // 폼 초기화
          setNewSurvey({
            survey_type: "bySubject",
            title: "",
            response_target: "student",
            status: "respondable",
            allow_question_add: true,
            is_anonymous: false,
            deadline: 0,
            teacher_editable: true,
            no_response_action: "none",
            include_tag: false,
            theme: "classic",
          });
        },
        onError: (error) => {
          console.error("설문 생성 오류:", error);
          handleToastCenterTop(
            t("common.survey.failCreate") + ": " + error.message,
            3000,
            "#dc3545"
          );
        },
      }
    );
  };

  const handleDeleteSurvey = (id) => {
    // 선택된 설문의 질문 데이터로 질문 존재 여부 확인
    const hasQuestions =
      selectedSurvey?.id === id &&
      surveyQuestionsQuery.data &&
      surveyQuestionsQuery.data.length > 0;

    if (hasQuestions) {
      handleToastCenterTop(
        t("common.survey.deleteWithQuestions"),
        5000,
        "#dc3545"
      );
      return;
    }

    setToastConfirmModalState({
      visible: true,
      message: t("common.survey.deleteConfirm"),
      onConfirm: () => {
        deleteSurvey.mutate(id, {
          onSuccess: () => {
            handleToastCenterTop(
              t("common.survey.successDelete"),
              3000,
              "#28a745"
            );
            if (selectedSurvey && selectedSurvey.id === id) {
              setSelectedSurvey(null);
            }
          },
          onError: (error) => {
            console.error("설문 삭제 오류:", error);
            handleToastCenterTop(
              t("common.survey.failDelete") + ": " + error.message,
              3000,
              "#dc3545"
            );
          },
        });
      },
      onCancel: () => {
        // 취소 시 아무것도 하지 않음
      },
    });
  };

  // 설문 업데이트 함수 (테마 등)
  const handleUpdateSurvey = (updatedData) => {
    if (!selectedSurvey) return;

    // theme 데이터만 있는 경우 체크
    const isThemeOnlyUpdate =
      Object.keys(updatedData).length === 1 &&
      updatedData.hasOwnProperty("theme");

    updateSurvey.mutate(
      {
        ...selectedSurvey,
        ...updatedData,
      },
      {
        onSuccess: () => {
          // theme 데이터만 있는 경우 토스트 표시 안 함
          if (!isThemeOnlyUpdate) {
            handleToastCenterTop(
              t("common.survey.successUpdate"),
              3000,
              "#28a745"
            );
          }
          // selectedSurvey 상태 업데이트
          setSelectedSurvey((prev) => ({ ...prev, ...updatedData }));

          // editingSurveys에서 해당 설문의 편집 상태도 초기화 (이미 서버에 저장됨)
          if (selectedSurvey?.id) {
            setEditingSurveys((prev) => {
              const newState = { ...prev };
              delete newState[selectedSurvey.id];
              return newState;
            });
          }
        },
        onError: (error) => {
          console.error("설문 업데이트 오류:", error);
          handleToastCenterTop(
            t("common.survey.failUpdate") + ": " + error.message,
            3000,
            "#dc3545"
          );
        },
      }
    );
  };

  if (isLoading) {
    return <div>Loading...</div>;
  }

  if (isError) {
    return (
      <div style={{ padding: "20px", textAlign: "center", color: "#dc3545" }}>
        <div style={{ fontSize: "18px", marginBottom: "10px" }}>❌</div>
        <div>{t("common.survey.loadingData")}</div>
        <button
          style={{ marginTop: "10px", padding: "8px 16px" }}
          onClick={() => surveysQuery.refetch()}
        >
          {t("common.survey.retryButton")}
        </button>
      </div>
    );
  }

  // 선택된 설문의 표시용 데이터 (원본 + 수정중인 데이터)
  const displayedSelectedSurvey = selectedSurvey
    ? getDisplaySurvey(selectedSurvey)
    : null;

  return (
    <div style={{ padding: "20px" }}>
      <h2 style={{ marginBottom: "20px", color: "#333" }}>
        {t("common.survey.title")}
      </h2>

      <SurveySettingTable
        surveys={surveys}
        selectedSurvey={selectedSurvey}
        onRowClick={handleRowClick}
        onSurveyChange={handleSurveyChange}
        onSurveySave={handleSurveySave}
        onAddSurvey={handleAddSurvey}
        onDeleteSurvey={handleDeleteSurvey}
        newSurvey={newSurvey}
        onInputChange={handleInputChange}
        getDisplaySurvey={getDisplaySurvey}
        isCreating={isCreating}
        isUpdating={isUpdating}
        isDeleting={isDeleting}
      />

      <MakingSurvey
        selectedSurvey={displayedSelectedSurvey}
        onUpdateSurvey={handleUpdateSurvey}
      />
    </div>
  );
}
